import { z } from "zod";

import { assetSchema } from "./asset";

export const testimonialSchema = z.object({
  avatar: z.string().optional(),
  name: z.string(),
  title: z.string(),
  csat: z.number(),
  designerId: z.string().optional(),
  text: z.string().optional(),
});

export type Testimonial = z.infer<typeof testimonialSchema>;

export const enterpriseUserPartialSchema = z.object({
  _id: z.string().optional(),
  name: z.string().optional(),
  email: z.string().optional(),
  avatar: z.string().optional(),
  role: z.string().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  profile: z.record(z.unknown()).optional(),
});

export type EnterpriseUserPartial = z.infer<typeof enterpriseUserPartialSchema>;

export const enterpriseSkillRatingSchema = z.object({
  label: z.string(),
  experience: z.number().int().optional(),
  category: z.string().optional(),
});

export type EnterpriseSkillRating = z.infer<typeof enterpriseSkillRatingSchema>;

export enum EnterpriseProfileTagType {
  NoRatedOptions = "no-rated-options",
  RatedOptions = "rated-options",
  TextInput = "text",
}

export const enterpriseRatingMapSchema = z.object({
  label: z.string(),
  value: z.coerce.number().optional(),
});

export type EnterpriseRatingMap = z.infer<typeof enterpriseRatingMapSchema>;

export const aiAssetSchema = assetSchema
  .extend({
    publicUrl: z.string().optional(),
    processedFiles: z.array(z.string()).optional(),
    source: z.string().optional(),
  })
  .refine(
    (data) => {
      return data.publicUrl || data.source;
    },
    {
      message: "publicUrl or source is required",
    },
  );

export const enterpriseCommentsAssistantSchema = z.object({
  assistantId: z.string().optional(), // OpenAI Assistant ID
  summary: z.string().optional(),
  instructions: z.string().optional(),
  files: z.array(aiAssetSchema).optional(),
});

export type EnterpriseCommentsAssistant = z.infer<
  typeof enterpriseCommentsAssistantSchema
>;

export const enterpriseBriefGuidelines = z.object({
  description: z.string().optional(),
  files: z.array(aiAssetSchema).optional(),
  assistantId: z.string().optional(), // OpenAI Assistant ID
  summary: z.string().optional(),
  checklist: z.string().optional(),
  runId: z.string().optional(),
  templates: z.array(aiAssetSchema).optional(),
});

export type EnterpriseBriefGuidelines = z.infer<
  typeof enterpriseBriefGuidelines
>;

export const enterpriseAIPersonas = z.object({
  name: z.string(),
  enabled: z.boolean(),
  default: z.boolean(),
  checklist: z.string().optional(),
});

export type EnterpriseAIPersonas = z.infer<typeof enterpriseAIPersonas>;

export const enterpriseAskPunttSkillSchema = z.object({
  skillId: z.string(),
  name: z.string(),
  enabled: z.boolean(),
  customInstructions: z.string().optional(),
  researchUrls: z.string().optional(),
});

export type EnterpriseAskPunttSkill = z.infer<
  typeof enterpriseAskPunttSkillSchema
>;

export const defaultAskPunttSkills: EnterpriseAskPunttSkill[] = [
  { skillId: "artwork_review", name: "Artwork Review", enabled: true },
  { skillId: "compare", name: "Compare", enabled: true },
  { skillId: "review", name: "Review", enabled: true },
  { skillId: "brief_creation", name: "Brief Writing", enabled: false },
  { skillId: "compliance_review", name: "Compliance Review", enabled: true },
  { skillId: "claim_generation", name: "Claims Verification", enabled: true },
];

export const enterpriseAskPunttConnectorSchema = z.object({
  connectorId: z.string(),
  name: z.string(),
  enabled: z.boolean(),
  connected: z.boolean().optional(),
});

export type EnterpriseAskPunttConnector = z.infer<
  typeof enterpriseAskPunttConnectorSchema
>;

export const enterpriseCustomWorkflowSchema = z.object({
  workflowId: z.string(),
  name: z.string(),
  trigger: z.string(),
  steps: z.string(),
  toolAccess: z.array(z.string()).optional(),
  webSearchEnabled: z.boolean().optional(),
  webSearchDomains: z.array(z.string()).optional(),
  enabled: z.boolean(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type EnterpriseCustomWorkflow = z.infer<
  typeof enterpriseCustomWorkflowSchema
>;

export const defaultAskPunttConnectors: EnterpriseAskPunttConnector[] = [
  { connectorId: "wrike", name: "Wrike", enabled: true, connected: false },
  {
    connectorId: "sharepoint",
    name: "SharePoint",
    enabled: true,
    connected: false,
  },
  { connectorId: "asana", name: "Asana", enabled: true, connected: false },
  { connectorId: "figma", name: "Figma", enabled: true, connected: false },
  {
    connectorId: "google_drive",
    name: "Google Drive",
    enabled: true,
    connected: false,
  },
  { connectorId: "canva", name: "Canva", enabled: true, connected: false },
];

export const enterpriseAIPersonasUpdate = z.object({
  name: z.string(),
  enabled: z.boolean().optional(),
  default: z.boolean().optional(),
});

export type EnterpriseAIPersonasUpdate = z.infer<
  typeof enterpriseAIPersonasUpdate
>;

export const catalystTeamSettingsSchema = z.object({
  briefGuidelines: enterpriseBriefGuidelines.optional(),
  complianceGuidelines: enterpriseBriefGuidelines.optional(),
  guidelines: enterpriseBriefGuidelines.optional(),
  commentsAssistant: enterpriseCommentsAssistantSchema.optional(),
  objectives: z.string().optional(),
  aiPersonas: z.array(enterpriseAIPersonas).optional(),
  askPunttSkills: z.array(enterpriseAskPunttSkillSchema).optional(),
  askPunttConnectors: z.array(enterpriseAskPunttConnectorSchema).optional(),
  customWorkflows: z.array(enterpriseCustomWorkflowSchema).optional(),
});

export type CatalystTeamSettings = z.infer<typeof catalystTeamSettingsSchema>;

export enum AIProvider {
  OpenAI = "openai",
  OpenAIReasoning = "openai-reasoning",
  Google = "google",
  Anthropic = "anthropic",
}

export const enterpriseProfileSettingsSchema =
  catalystTeamSettingsSchema.extend({
    skills: z.array(enterpriseSkillRatingSchema).optional(),
    industries: z.array(enterpriseSkillRatingSchema).optional(),
    tools: z.array(enterpriseSkillRatingSchema).optional(),
    softSkills: z.array(enterpriseSkillRatingSchema).optional(),
    growthAreas: z.array(z.string()).optional(),
    ratings: z.array(enterpriseRatingMapSchema).optional(),

    // Catalyst settings
    aiProviders: z.nativeEnum(AIProvider).array().optional(),
    delayedMailTime: z.coerce.number().optional(),
    extraFonts: z.array(z.string()).optional(),
  });

export type EnterpriseProfileSettings = z.infer<
  typeof enterpriseProfileSettingsSchema
>;

export const enterpriseTeamSchema = z.object({
  _id: z.string().optional(),
  users: z.array(z.union([z.string(), enterpriseUserPartialSchema])),
  profileSettings: catalystTeamSettingsSchema.optional(),
});

export const wrikeTaskStatusEnum = z.enum([
  "Active",
  "Completed",
  "Deferred",
  "Cancelled",
]);

export const integrationTriggerConfigSchema = z.object({
  eventType: z.string().optional(),
  statusName: z.string().optional(),
  skipPunttUserCheck: z.boolean().optional(),
  assignSelfOnTrigger: z.boolean().optional(),
});

export type IntegrationTriggerConfig = z.infer<
  typeof integrationTriggerConfigSchema
>;

export const integrationCommentBehaviorSchema = z.object({
  postProgressComments: z.boolean().optional(), // When false, skip in-progress/error comments (default: true)
  postCompletionComments: z.boolean().optional(), // When false, skip completion/share-link comments (default: true)
  skipAllComments: z.boolean().optional(), // When true, skip ALL comments (overrides granular controls)
  skipLinkUpdates: z.boolean().optional(), // When true, skip share-link comments only
});

export type IntegrationCommentBehavior = z.infer<
  typeof integrationCommentBehaviorSchema
>;

// NOTE: designReviewRules are partially implemented (christo queue only) — disabled in UI for now
export const designReviewRuleSchema = z.object({
  descriptionContains: z.string(),
  tagUsersOnCompletion: z.boolean().optional(),
  skipStatusUpdate: z.boolean().optional(),
});

export type DesignReviewRule = z.infer<typeof designReviewRuleSchema>;

export const wrikeSchema: z.ZodType = z.object({
  enabled: z.boolean().optional(),
  user: z.string().optional(),
  responseActions: z
    .object({
      updateTaskStatus: z.union([wrikeTaskStatusEnum, z.string()]).optional(),
      inProgressTaskStatus: z.string().optional(),
      tagUsers: z
        .array(
          z.object({
            email: z.string(),
          }),
        )
        .optional(),
    })
    .optional(),
  triggerConfig: integrationTriggerConfigSchema.optional(),
  commentBehavior: integrationCommentBehaviorSchema.optional(),
  designReviewRules: z.array(designReviewRuleSchema).optional(),
});

export type WrikeIntegration = z.infer<typeof wrikeSchema>;

export const asanaSchema = z.object({
  enabled: z.boolean().optional(),
  commentBehavior: integrationCommentBehaviorSchema.optional(),
  designReviewRules: z.array(designReviewRuleSchema).optional(),
});

export type AsanaIntegration = z.infer<typeof asanaSchema>;

export const emailReplySettingsSchema = z.object({
  replyWithLink: z.boolean().optional(), // Send review link in reply (default: true)
  replyWithComments: z.boolean().optional(), // Include AI comment summary in reply (default: false)
  replyPrompt: z.string().max(2000).optional(), // Instructions for AI reply content
});

export type EmailReplySettings = z.infer<typeof emailReplySettingsSchema>;

export const emailSchema = z.object({
  enabled: z.boolean().optional(), // OFF by default — must be explicitly enabled
  replySettings: emailReplySettingsSchema.optional(),
  allowedDomains: z.array(z.string()).optional(), // Restrict sender domains (empty = all allowed)
  allowedSenderPatterns: z.array(z.string()).optional(), // Regex patterns for sender email filtering
  commentBehavior: integrationCommentBehaviorSchema.optional(),
  inboundAddress: z.string().optional(), // Generated inbound email address
  connectionId: z.string().optional(), // UUID per enterprise
});

export type EmailIntegration = z.infer<typeof emailSchema>;

export const enterpriseSchema = z.object({
  _id: z.string().optional(),
  name: z.string(),
  slug: z.string().optional(),
  logo: z.string().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  primaryColor: z.string().optional(),
  hero: z.string().optional(),
  profile: z.record(z.unknown()).optional(),
  totalActiveGigs: z.number().optional(),
  totalInactiveGigs: z.number().optional(),
  industries: z.array(z.string()).optional(),
  testimonials: z.array(testimonialSchema).optional(),
  profileSettings: enterpriseProfileSettingsSchema.optional(),
  description: z.string().optional(),
  teams: z.array(enterpriseTeamSchema).optional(),
  integrations: z
    .object({
      wrike: wrikeSchema.optional(),
      asana: asanaSchema.optional(),
      email: emailSchema.optional(),
    })
    .optional(),
  isDemo: z.boolean().optional(),
});

export type Enterprise = z.infer<typeof enterpriseSchema>;

export const defaultAiPersonas: EnterpriseAIPersonas[] = [
  { name: "Brand", enabled: true, default: true },
  { name: "Flex", enabled: false, default: false },
  { name: "Legal", enabled: false, default: false },
  { name: "Copy", enabled: false, default: false },
];
